/**
 * Light hand-rolled User-Agent parser. We deliberately avoid pulling in a
 * dependency for this — the widget only needs a handful of coarse buckets
 * (browser family + rough version, OS family, device type) for bug-report
 * context, not exhaustive UA-sniffing accuracy.
 */

export type DeviceType = 'desktop' | 'mobile' | 'tablet';

export interface ParsedUserAgent {
  browser: string;
  browserVersion?: string;
  os: string;
  deviceType: DeviceType;
}

interface BrowserMatch {
  browser: string;
  version?: string;
}

/**
 * Order matters: Chromium-based Edge and Opera both include a `Chrome/`
 * token in their UA string, so they must be checked *before* Chrome, or
 * they'd be misreported as Chrome. Safari must be checked last since
 * Chrome/Edge/Opera UAs on most platforms also include a `Safari/` token.
 */
function detectBrowser(ua: string): BrowserMatch {
  let match = ua.match(/Edg(?:A|iOS)?\/([\d.]+)/);
  if (match) {
    return { browser: 'Edge', version: match[1] };
  }

  match = ua.match(/OPR\/([\d.]+)/) ?? ua.match(/Opera\/([\d.]+)/);
  if (match) {
    return { browser: 'Opera', version: match[1] };
  }

  match = ua.match(/Firefox\/([\d.]+)/);
  if (match) {
    return { browser: 'Firefox', version: match[1] };
  }

  match = ua.match(/Chrome\/([\d.]+)/);
  if (match) {
    return { browser: 'Chrome', version: match[1] };
  }

  if (/Safari\//.test(ua)) {
    const versionMatch = ua.match(/Version\/([\d.]+)/);
    return { browser: 'Safari', version: versionMatch?.[1] };
  }

  return { browser: 'Unknown' };
}

/**
 * iOS must be checked before macOS: an iPhone/iPad UA contains the
 * substring "like Mac OS X", which would otherwise be misread as macOS.
 * Android must be checked before Linux for the same reason (Android UAs
 * contain "Linux").
 */
function detectOS(ua: string): string {
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Macintosh|Mac OS X/.test(ua)) return 'macOS';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown';
}

/**
 * Device type from token hints. iPad UAs (iPadOS 13+, masquerading as
 * desktop Safari in most respects) still include an "iPad" token, so that
 * check must win over the generic "Mobile" check — iPad UAs often *also*
 * contain "Mobile/...". Android tablets conventionally omit the "Mobile"
 * token from their UA (unlike Android phones), so on Android we treat the
 * absence of "Mobile" as a tablet signal.
 */
function detectDeviceType(ua: string, os: string): DeviceType {
  if (/iPad/.test(ua) || /Tablet/.test(ua)) return 'tablet';
  if (os === 'Android' && !/Mobile/.test(ua)) return 'tablet';
  if (/Mobile/.test(ua)) return 'mobile';
  return 'desktop';
}

export function parseUserAgent(ua: string): ParsedUserAgent {
  const safeUa = ua ?? '';
  const os = detectOS(safeUa);
  const { browser, version } = detectBrowser(safeUa);
  const deviceType = detectDeviceType(safeUa, os);

  return { browser, browserVersion: version, os, deviceType };
}
